import {
  blobToken,
  createSupportTicket,
  createVendorExperience,
  forgotPassword,
  getSupportTicketById,
  getSupportTickets,
  getVendorAnalyticsBookingTrend,
  getVendorBookings,
  getVendorBookingTrend,
  getVendorDashboardOverview,
  getVendorEarningsTrend,
  getVendorExperienceById,
  getVendorExperiences,
  getVendorPayoutsEarnings,
  getVendorPayoutsSummary,
  getVendorProfile,
  getVendorRecentReviews,
  getVendorTopExperiences,
  registerAdmin,
  resendOTP,
  resetPassword,
  updateVendorExperience,
  updateVendorProfile,
  userLogin,
  userLogout,
  userSignUp,
  VerifyOTP,
  vendorSignUp,
  getStripeStatus,
  createStripeOnboardingLink,
  getStripeDashboardLink,
  getStripeUpdateLink,
  getPublicCategories,
  getPublicLocations,
  getPublicExperiences,
  getPublicExperiencesByCategory,
  getPublicExperiencesByLocation,
  getPublicExperienceById,
  getUserBookings,
  getUserBookingById,
  getUserProfile,
  updateUserProfile,
  getVendorBookingById,
  addSupportTicketComment,
  getAdminDashboardSummary,
  getAdminDashboardVendorGrowth,
  getAdminDashboardMonthlyBookings,
  getAdminVendorsList,
  getAdminVendorProfile,
  getAdminVendorActivate,
  getAdminVendorSuspend,
  getAdminVendorReject,
  getAdminVendorApprove,
  getAdminVendorExperiences,
  getAdminExperiences,
  getAdminExperienceReject,
  getAdminExperienceApprove,
  getAdminBookings,
  getAdminSupportTickets,
  getAdminPayouts,
  getAdminUsers,
  getAdminVendorById,
  getAdminExperienceById,
  getAdminBookingById,
  getAdminVendorsDropdown,
  stripeOnboarding,
  validateBooking,
  createCheckoutSession,
  vendorBookingComplete,
  vendorBookingConfirm,
  vendorSuggestAlternateTime,
  vendorBookingDeny,
  userAcceptAlternateTime,
  userDenyAlternateTime,
  userBookingsCompletion,
  getPaymentSuccessDetails,
  changeSupportTicketStatus,
  getAdminUserProfile,
  getDurations,
  submitBookingReview,
  firstFlightApply,
  getBookingCalendar,
  getVendorActiveAgreement,
  getVendorAcceptAgreement,
  getUserActiveAgreement,
  getUserAcceptAgreement,
  getUserBookingActiveAgreement,
  getPublicExperienceDetails,
  getPublicVendorProfile,
  validateBookingGuest,
  createCheckoutSessionGuest,
  getPaymentSuccessDetailsGuest,
  bookingCancel,
  VerifyOTPNoAuth,
} from "../api/api-methods";
import { useMutation, useQuery } from "@tanstack/react-query";

export const useUserLogin = () => {
  return useMutation({
    mutationFn: (data) => userLogin(data),
  });
};

export const useUserLogout = () => {
  return useMutation({
    mutationFn: () => userLogout(),
  });
};

export const useRegisterAdmin = () => {
  return useMutation({
    mutationFn: () => registerAdmin(),
  });
};

export const useUserSignUp = () => {
  return useMutation({
    mutationFn: (userData) => userSignUp(userData),
  });
};

export const useVerifyOTP = () => {
  return useMutation({
    mutationFn: (data) => VerifyOTP(data),
  });
};
export const useVerifyOTPNoAuth = () => {
  return useMutation({
    mutationFn: (data) => VerifyOTPNoAuth(data),
  });
};

export const useResendOTP = () => {
  return useMutation({
    mutationFn: () => resendOTP(),
  });
};

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: (data) => forgotPassword(data),
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: (data) => resetPassword(data),
  });
};

export const useVendorSignUp = () => {
  return useMutation({
    mutationFn: (data) => vendorSignUp(data),
  });
};

export const useBlobToken = () => {
  return useMutation({
    mutationFn: (data) => blobToken(data),
  });
};

// Vendor Dashboard Queries
export const useVendorDashboardOverview = () => {
  return useQuery({
    queryKey: ["vendorDashboardOverview"],
    queryFn: getVendorDashboardOverview,
  });
};

export const useVendorBookingTrend = (year) => {
  return useQuery({
    queryKey: ["vendorBookingTrend", year],
    queryFn: () => getVendorBookingTrend(year),
    enabled: !!year,
  });
};

export const useVendorBookings = ({ page, pageSize, search, status }) => {
  return useQuery({
    queryKey: ["vendorBookings", page, pageSize, search, status],
    queryFn: () => getVendorBookings({ page, pageSize, search, status }),
    keepPreviousData: true,
  });
};

// Vendor Analytics Queries
export const useVendorEarningsTrend = ({ range, year }) => {
  return useQuery({
    queryKey: ["vendorEarningsTrend", range, year],
    queryFn: () => getVendorEarningsTrend({ range, year }),
    enabled: !!range,
  });
};

export const useVendorAnalyticsBookingTrend = () => {
  return useQuery({
    queryKey: ["vendorAnalyticsBookingTrend"],
    queryFn: getVendorAnalyticsBookingTrend,
  });
};

export const useVendorTopExperiences = ({ page, pageSize, search }) => {
  return useQuery({
    queryKey: ["vendorTopExperiences", page, pageSize, search],
    queryFn: () => getVendorTopExperiences({ page, pageSize, search }),
    keepPreviousData: true,
  });
};

export const useVendorRecentReviews = ({ page, pageSize }) => {
  return useQuery({
    queryKey: ["vendorRecentReviews", page, pageSize],
    queryFn: () => getVendorRecentReviews({ page, pageSize }),
    keepPreviousData: true,
  });
};

// Vendor Payout Queries
export const useVendorPayoutsSummary = () => {
  return useQuery({
    queryKey: ["vendorPayoutsSummary"],
    queryFn: getVendorPayoutsSummary,
  });
};

export const useVendorPayoutsEarnings = ({
  page,
  pageSize,
  tab,
  dateFrom,
  dateTo,
  earningStatus,

  search,
}) => {
  return useQuery({
    queryKey: [
      "vendorPayoutsEarnings",
      page,
      pageSize,
      tab,
      dateFrom,
      dateTo,
      earningStatus,

      search,
    ],
    queryFn: () =>
      getVendorPayoutsEarnings({
        page,
        pageSize,
        tab,
        dateFrom,
        dateTo,
        earningStatus,
        search,
      }),
    keepPreviousData: true,
  });
};

// Vendor Experiences Queries
export const useVendorExperiences = ({
  page,
  pageSize,
  status,
  category,
  search,
}) => {
  return useQuery({
    queryKey: ["vendorExperiences", page, pageSize, status, category, search],
    queryFn: () =>
      getVendorExperiences({ page, pageSize, status, category, search }),
    keepPreviousData: true,
  });
};

export const useVendorExperienceById = (experienceId) => {
  return useQuery({
    queryKey: ["vendorExperience", experienceId],
    queryFn: () => getVendorExperienceById(experienceId),
    enabled: !!experienceId,
  });
};

export const useCreateVendorExperience = () => {
  return useMutation({
    mutationFn: (data) => createVendorExperience(data),
  });
};

export const useUpdateVendorExperience = () => {
  return useMutation({
    mutationFn: ({ experienceId, data }) =>
      updateVendorExperience({ experienceId, data }),
  });
};

// Support Ticket Queries (used by all roles)
export const useSupportTickets = ({
  page,
  pageSize,
  status,
  search,
  sort,
  role,
}) => {
  return useQuery({
    queryKey: ["supportTickets", page, pageSize, status, search, sort, role],
    queryFn: () =>
      getSupportTickets({ page, pageSize, status, search, sort, role }),
    keepPreviousData: true,
  });
};

export const useCreateSupportTicket = (role) => {
  return useMutation({
    mutationFn: ({ data, role }) => {
      createSupportTicket(data, role);
    },
  });
};

export const useSupportTicketById = (ticketId, role) => {
  return useQuery({
    queryKey: ["supportTicketById", ticketId, role],
    queryFn: () => getSupportTicketById(ticketId, role),
    enabled: !!ticketId && !!role, // 👈 jab tak dono na ho tab tak call nahi
  });
};

// Vendor Profile Queries
export const useVendorProfile = () => {
  return useQuery({
    queryKey: ["vendorProfile"],
    queryFn: getVendorProfile,
  });
};

export const useUpdateVendorProfile = () => {
  return useMutation({
    mutationFn: (data) => updateVendorProfile(data),
  });
};

// Stripe Payout Queries
export const useStripeStatus = () => {
  return useQuery({
    queryKey: ["stripeStatus"],
    queryFn: getStripeStatus,
  });
};

export const useCreateStripeOnboardingLink = () => {
  return useMutation({
    mutationFn: () => createStripeOnboardingLink(),
  });
};

export const useStripeDashboardLink = () => {
  return useMutation({
    mutationFn: () => getStripeDashboardLink(),
  });
};

export const useStripeUpdateLink = () => {
  return useMutation({
    mutationFn: () => getStripeUpdateLink(),
  });
};

// Vendor Booking Queries
export const useVendorBookingById = (bookingId) => {
  return useQuery({
    queryKey: ["vendorBooking", bookingId],
    queryFn: () => getVendorBookingById(bookingId),
    enabled: !!bookingId,
  });
};
// Public Queries (User Homepage)
export const usePublicCategories = () => {
  return useQuery({
    queryKey: ["publicCategories"],
    queryFn: getPublicCategories,
  });
};

export const usePublicLocations = () => {
  return useQuery({
    queryKey: ["publicLocations"],
    queryFn: getPublicLocations,
  });
};

export const usePublicExperiences = ({
  page,
  pageSize,
  isTop,
  search,
  categoryId,
  city,
  country,
  priceLowToHigh,
  priceHighToLow,
  duration,
  userId,
}) => {
  return useQuery({
    queryKey: [
      "publicExperiences",
      page,
      pageSize,
      isTop,
      search,
      categoryId,
      city,
      country,
      priceLowToHigh,
      priceHighToLow,
      duration,
      userId,
    ],
    queryFn: () =>
      getPublicExperiences({
        page,
        pageSize,
        isTop,
        search,
        categoryId,
        city,
        country,
        priceLowToHigh,
        priceHighToLow,
        duration,
        userId,
      }),
    keepPreviousData: true,
  });
};
export const useDurations = () => {
  return useQuery({
    queryKey: ["durations"],
    queryFn: getDurations,
    staleTime: 1000 * 60 * 5, // 5 min cache (optional but recommended)
  });
};
export const usePublicExperiencesByCategory = ({
  categoryId,
  page,
  pageSize,
  search,
}) => {
  return useQuery({
    queryKey: [
      "publicExperiencesByCategory",
      categoryId,
      page,
      pageSize,
      search,
    ],
    queryFn: () =>
      getPublicExperiencesByCategory({ categoryId, page, pageSize, search }),
    enabled: !!categoryId,
    keepPreviousData: true,
  });
};

export const usePublicExperiencesByLocation = ({
  locationId,
  page,
  pageSize,
  search,
}) => {
  return useQuery({
    queryKey: [
      "publicExperiencesByLocation",
      locationId,
      page,
      pageSize,
      search,
    ],
    queryFn: () =>
      getPublicExperiencesByLocation({ locationId, page, pageSize, search }),
    enabled: !!locationId,
    keepPreviousData: true,
  });
};

export const usePublicExperienceById = (experienceId) => {
  return useQuery({
    queryKey: ["publicExperience", experienceId],
    queryFn: () => getPublicExperienceById(experienceId),
    enabled: !!experienceId,
  });
};

// User Bookings Query
export const useUserBookings = ({ page, pageSize, status, search }) => {
  return useQuery({
    queryKey: ["userBookings", page, pageSize, status, search],
    queryFn: () => getUserBookings({ page, pageSize, status, search }),
    keepPreviousData: true,
  });
};

export const useUserBookingById = (bookingId) => {
  return useQuery({
    queryKey: ["userBooking", bookingId],
    queryFn: () => getUserBookingById(bookingId),
    enabled: !!bookingId,
  });
};

// User Profile Queries
export const useUserProfile = () => {
  return useQuery({
    queryKey: ["userProfile"],
    queryFn: getUserProfile,
  });
};

export const useUpdateUserProfile = () => {
  return useMutation({
    mutationFn: (data) => updateUserProfile(data),
  });
};

export const useAddSupportTicketComment = (role) => {
  return useMutation({
    mutationFn: ({ ticketId, message }) =>
      addSupportTicketComment(ticketId, role, message),
  });
};

// admin apis

export const useAdminDashboardSummary = () => {
  return useQuery({
    queryKey: ["adminDashboardSummary"],
    queryFn: getAdminDashboardSummary,
  });
};
export const useAdminDashboardVendorGrowth = (year) => {
  return useQuery({
    queryKey: ["adminDashboardVendorGrowth", year],
    queryFn: () => getAdminDashboardVendorGrowth(year),
  });
};
export const useAdminDashboardMonthlyBookings = (year) => {
  return useQuery({
    queryKey: ["adminDashboardMonthlyBookings", year],
    queryFn: () => getAdminDashboardMonthlyBookings(year),
  });
};

export const useAdminVendorsList = ({ page, pageSize, status, search }) => {
  return useQuery({
    queryKey: ["adminVendorsList", page, pageSize, status, search],
    queryFn: () => getAdminVendorsList({ page, pageSize, status, search }),
  });
};

export const useGetAdminVendorProfile = (vendorId) => {
  return useQuery({
    queryKey: ["adminVendorProfile", vendorId],
    queryFn: () => getAdminVendorProfile(vendorId),
    enabled: !!vendorId,
  });
};

export const useAdminVendorApprove = () => {
  return useMutation({
    mutationFn: (vendorId) => getAdminVendorApprove(vendorId),
  });
};

export const useAdminVendorReject = () => {
  return useMutation({
    mutationFn: ({ vendorId, reason }) =>
      getAdminVendorReject(vendorId, reason),
  });
};

export const useAdminVendorSuspend = () => {
  return useMutation({
    mutationFn: ({ vendorId, reason }) =>
      getAdminVendorSuspend(vendorId, reason),
  });
};

export const useAdminVendorActivate = () => {
  return useMutation({
    mutationFn: (vendorId) => getAdminVendorActivate(vendorId),
  });
};

export const useAdminExperiences = ({
  currentPage,
  pageSize,
  search,
  vendorId,
  status,
}) => {
  return useQuery({
    queryKey: [
      "adminExperiences",
      currentPage,
      pageSize,
      search,
      vendorId,
      status,
    ],
    queryFn: () =>
      getAdminExperiences({
        currentPage,
        pageSize,
        search,
        vendorId,
        status,
      }),
    keepPreviousData: true, // pagination smooth rahe
  });
};

export const useAdminExperienceApprove = () => {
  return useMutation({
    mutationFn: (experienceId) => getAdminExperienceApprove(experienceId),
  });
};

// ❌ Reject Experience
export const useAdminExperienceReject = () => {
  return useMutation({
    mutationFn: ({ experienceId, reason }) =>
      getAdminExperienceReject({ experienceId, reason }),
  });
};

export const useAdminBookings = ({
  page,
  pageSize,
  search,
  vendorId,
  status,
}) => {
  return useQuery({
    queryKey: ["adminBookings", page, pageSize, search, vendorId, status],
    queryFn: () =>
      getAdminBookings({
        page,
        pageSize,
        search,
        vendorId,
        status,
      }),
    keepPreviousData: true,
  });
};

export const useAdminSupportTickets = ({
  page,
  pageSize,
  search,
  type,
  status,
  priority,
}) => {
  return useQuery({
    queryKey: [
      "adminSupportTickets",
      page,
      pageSize,
      search,
      type,
      status,
      priority,
    ],
    queryFn: () =>
      getAdminSupportTickets({
        page,
        pageSize,
        search,
        type,
        status,
        priority,
      }),
    keepPreviousData: true,
  });
};

export const useAdminPayouts = ({
  page,
  pageSize,
  search,
  status,
  fromDate,
  toDate,
}) => {
  return useQuery({
    queryKey: [
      "adminPayouts",
      page,
      pageSize,
      search,
      status,
      fromDate,
      toDate,
    ],
    queryFn: () =>
      getAdminPayouts({
        page,
        pageSize,
        search,
        status,
        fromDate,
        toDate,
      }),
  });
};

export const useAdminUsers = ({ page, pageSize, search, status }) => {
  return useQuery({
    queryKey: ["adminUsers", page, pageSize, search, status],
    queryFn: () =>
      getAdminUsers({
        page,
        pageSize,
        search,
        status,
      }),
  });
};

export const useAdminVendorById = (vendorId) => {
  return useQuery({
    queryKey: ["adminVendorProfile", vendorId],
    queryFn: () => getAdminVendorById(vendorId),
  });
};

export const useAdminExperienceById = (experienceId) => {
  return useQuery({
    queryKey: ["adminExperienceById", experienceId],
    queryFn: () => getAdminExperienceById(experienceId),
    enabled: !!experienceId,
  });
};

export const useAdminBookingById = (bookingId) => {
  return useQuery({
    queryKey: ["admin-booking-detail", bookingId],
    queryFn: () => getAdminBookingById(bookingId),
    enabled: !!bookingId,
  });
};

export const useAdminVendorsDropdown = () => {
  return useQuery({
    queryKey: ["adminVendorsDropdown"],
    queryFn: getAdminVendorsDropdown,
  });
};

export const useStripeOnboarding = () => {
  return useMutation({
    mutationFn: stripeOnboarding,
  });
};

export const useValidateBooking = () => {
  return useMutation({
    mutationFn: validateBooking,
  });
};
export const useValidateBookingGuest = () => {
  return useMutation({
    mutationFn: validateBookingGuest,
  });
};

export const useCreateCheckoutSession = () => {
  return useMutation({
    mutationFn: createCheckoutSession,
  });
};
export const useCreateCheckoutSessionGuest = () => {
  return useMutation({
    mutationFn: createCheckoutSessionGuest,
  });
};

export const useVendorBookingComplete = () =>
  useMutation({
    mutationFn: vendorBookingComplete,
  });

export const useVendorBookingConfirm = () =>
  useMutation({
    mutationFn: vendorBookingConfirm,
  });

export const useVendorSuggestAlternateTime = () =>
  useMutation({
    mutationFn: vendorSuggestAlternateTime,
  });

export const useVendorBookingDeny = () =>
  useMutation({
    mutationFn: vendorBookingDeny,
  });

export const useUserAcceptAlternateTime = () =>
  useMutation({
    mutationFn: userAcceptAlternateTime,
  });

export const useUserDenyAlternateTime = () =>
  useMutation({
    mutationFn: userDenyAlternateTime,
  });

export const useUserBookingComplete = () =>
  useMutation({
    mutationFn: userBookingsCompletion,
  });

export const usePaymentSuccessDetails = (tempBookingId, token) => {
  return useQuery({
    queryKey: ["paymentSuccessDetails", tempBookingId],
    queryFn: () => getPaymentSuccessDetails(tempBookingId, token),
    enabled: !!tempBookingId && !!token,
  });
};
export const usePaymentSuccessDetailsGuest = (tempBookingId) => {
  return useQuery({
    queryKey: ["paymentSuccessDetailsGuest", tempBookingId],
    queryFn: () => getPaymentSuccessDetailsGuest(tempBookingId),
    enabled: !!tempBookingId,
  });
};

export const useChangeSupportTicketStatus = () => {
  return useMutation({
    mutationFn: changeSupportTicketStatus,
  });
};

export const useGetAdminUserProfile = (userId) => {
  return useQuery({
    queryKey: ["admin-user-profile", userId],
    queryFn: () => getAdminUserProfile({ userId }),
    enabled: !!userId,
  });
};

export const useSubmitBookingReview = () => {
  return useMutation({
    mutationFn: submitBookingReview,
  });
};

export const useFirstFlightApply = () => {
  return useMutation({
    mutationFn: firstFlightApply,
  });
};

export const useBookingCalendar = () => {
  return useMutation({
    mutationFn: (bookingId) => {
      getBookingCalendar(bookingId);
    },
  });
};

export const useGetVendorActiveAgreement = () => {
  return useQuery({
    queryKey: ["vendor-active-agreement"],
    queryFn: () => getVendorActiveAgreement(),
  });
};
export const useAcceptVendorAgreement = () => {
  return useMutation({
    mutationKey: ["vendor-accept-agreement"],
    mutationFn: (payload) => getVendorAcceptAgreement(payload),
  });
};
export const useGetUserActiveAgreement = () => {
  return useQuery({
    queryKey: ["user-active-agreement"],
    queryFn: () => getUserActiveAgreement(),
  });
};
export const useAcceptUserAgreement = () => {
  return useMutation({
    mutationKey: ["user-accept-agreement"],
    mutationFn: (payload) => getUserAcceptAgreement(payload),
  });
};

export const useGetUserBookingActiveAgreement = () => {
  return useQuery({
    queryKey: ["user-booking-active-agreement"],
    queryFn: () => getUserBookingActiveAgreement(),
  });
};

export const usePublicExperienceDetails = (experienceSlug) => {
  return useQuery({
    queryKey: ["publicExperienceDetails", experienceSlug],
    queryFn: () => getPublicExperienceDetails(experienceSlug),
    enabled: !!experienceSlug,
  });
};

export const usePublicVendorProfile = (vendorSlug) => {
  return useQuery({
    queryKey: ["publicVendorProfile", vendorSlug],
    queryFn: () => getPublicVendorProfile(vendorSlug),
    enabled: !!vendorSlug, // slug ho tabhi call ho
  });
};

export const useBookingCancel = () => {
  return useMutation({
    mutationKey: ["booking-cancel"],
    mutationFn: bookingCancel,
  });
};
